import { jwtDecode, JwtPayload } from "jwt-decode";
import { oneLinkIdsConfig, r2cIdsConfig } from "../../Config/idsConfig";
import { clearLocalStorageToken, setAuthError, validateTokenAndGetUserInfo } from "../../store/slices/authSlice";
import { AppDispatch } from "../../store/store";
import { ErrorType } from "../../types/errorType";
import { IDSorganisation } from "types/idsOrganisation";
import { getUserInfo } from "store/slices/userSlice";

/**
 * Validates the access token, fetches user information, and handles any errors that occur.
 * @param {AppDispatch} dispatch - The dispatch function from Redux.
 * @param {string} referrerUrl - The URL to redirect the user back to in case of an error.
 * @returns {Promise<void>}
 */
export const validateTokenAndFetchUserInfo = async (dispatch: AppDispatch, referrerUrl: string): Promise<void> => {
  try {
    const actionResult = await dispatch(validateTokenAndGetUserInfo());
    const userOrganisation = actionResult.payload as IDSorganisation;

    if (userOrganisation.sub) {
      await dispatch(getUserInfo(userOrganisation.sub as string));
    }
  } catch (error: any) {
    handleAuthError(dispatch, error, referrerUrl);
  }
};

/**
 * Handles authentication errors by dispatching an error action with the provided message.
 * @param dispatch - The dispatch function from Redux to dispatch actions.
 * @param error - (optional) An optional parameter representing the error object. If provided, its message will be used.
 * @param referrerUrl - (optional) The URL to redirect the user back to.
 */
export const handleAuthError = (dispatch: AppDispatch, error?: any | null, referrerUrl?: string) => {
  const errorMessage = error?.message || "Your user name and password cannot be authenticated.";

  // Default error type response
  const errorTypeResponse: ErrorType = {
    title: "Unauthorized",
    statusCode: 401,
    messages: [{ message: errorMessage, code: "" }],
  };

  // If a referrer URL is provided, add it to the error message
  if (referrerUrl) {
    errorTypeResponse.messages.push({
      message: `<a href="${referrerUrl}" rel="noopener noreferrer">Please click to go back.</a>`,
      code: "",
    });
  }

  // Dispatch the error action
  dispatch(setAuthError(errorTypeResponse));
};

/**
 * Returns the referrer URL based on the provided issuer.
 * This function maps an issuer to its corresponding referrer URL,
 * which can be used to redirect users back to the original site.
 *
 * @param issuer - The issuer string to match against known issuers.
 * @returns The corresponding referrer URL if the issuer matches, or an empty string if there is no match.
 * Note: The function relies on configuration objects (r2cIdsConfig and oneLinkIdsConfig)
 *       to retrieve the issuer and referrer URL pairs.
 */
export const getReferrerUrl = (issuer: string): string => {
  switch (issuer) {
    case r2cIdsConfig.issuer:
      return r2cIdsConfig.referrerUrl as string;
    case oneLinkIdsConfig.issuer:
      return oneLinkIdsConfig.referrerUrl as string;
    default:
      return "";
  }
};

/* 
  Description: Extracts the 'code' and 'code_verifier' parameters from the provided URLSearchParams object.
  Parameters:
     - queryParams: The URLSearchParams object containing query parameters.
  Returns:
     - An object containing the extracted 'code' and 'code_verifier' parameters.
*/
export const getCodeParams = (queryParams: URLSearchParams) => {
  const code = queryParams.get("code");
  const codeVerifier = queryParams.get("code_verifier");
  return { code, codeVerifier };
};

/* 
Description: Decodes the provided code using jwtDecode and extracts the issuer ('iss') from the decoded JWT payload.
Parameters:
  - code: The code to decode.
  - dispatch: The dispatch function from Redux to dispatch actions.
Returns:
  - The issuer ('iss') extracted from the decoded JWT payload, or undefined if decoding fails.
*/
export const getIDSIssuer = (code: string, dispatch: AppDispatch) => {
  try {
    const decodedCode: JwtPayload = jwtDecode(code);
    const idsIssuer = decodedCode.iss as string;
    dispatch(clearLocalStorageToken()); //remove any exisiting local storage tokens has you wil be getting new ones
    return idsIssuer;
  } catch (error) {
    handleAuthError(dispatch, error);
    return undefined;
  }
};
